import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import {
  TrendingUp,
  Schedule,
  AccountBalance,
  Settings as SettingsIcon,
  CheckCircle,
  Warning
} from '@mui/icons-material';
import calculatorApi from '../services/calculatorApi';
import CompactTradingHours from './CompactTradingHours';
import './BusinessSettings.css';

const BusinessSettings = () => {
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState({
    monthly_profit_target: '',
    annual_overhead: 0,
    weeks_per_year: '52',
    staff_utilization_rate: '85',
    total_working_days: 0,
    total_working_hours: 0,
    profit_level_1: '',
    profit_level_2: '',
    vat_rate: '20',
    is_vat_registered: false,
    annual_profit_target: 0,
    weekly_profit_target: 0,
    overhead_per_minute: 0,
    owner_profit_per_minute: 0,
    support_staff_per_minute: 0,
    total_annual_hours: 0,
    effective_hours: 0
  });
  
  const [tradingHoursData, setTradingHoursData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Check if setup is complete
  // NOTE: profit_level_1 / profit_level_2 deliberately excluded — the flat
  // profit-margin % was retired from pricing (19 Aug 2026). Completion is now
  // based purely on the real cost/profit inputs the calculator actually uses.
  const isComplete = useMemo(() => {
    return !!(
      settings.monthly_profit_target && 
      settings.overhead_per_minute && 
      settings.owner_profit_per_minute && 
      settings.total_working_hours
    );
  }, [settings]);

  // Load settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        
        // Fetch both business settings and trading hours
        const [businessData, tradingHours] = await Promise.all([
          calculatorApi.getBusinessSettings(),
          calculatorApi.getTradingHours()
        ]);
        
        if (businessData && Object.keys(businessData).length > 0) {
          setSettings(prev => ({
            ...prev,
            ...businessData
          }));
        }
        
        if (tradingHours) {
          setTradingHoursData(tradingHours);
          
          // Update settings with trading hours data
          if (tradingHours.total_weekly_hours || tradingHours.total_working_days) {
            setSettings(prev => ({
              ...prev,
              total_working_hours: tradingHours.total_weekly_hours || 0,
              total_working_days: tradingHours.total_working_days || 0
            }));
          }
        }
      } catch (err) {
        console.error('Error loading business settings:', err);
        setError('Failed to load settings. Using defaults.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Calculate derived values when base values change
useEffect(() => {
  const timer = setTimeout(() => {
    const monthlyProfit = parseFloat(settings.monthly_profit_target) || 0;
    const weeklyHours = parseFloat(settings.total_working_hours) || 0;
    const weeksPerYear = parseFloat(settings.weeks_per_year) || 52;
    const utilizationRate = parseFloat(settings.staff_utilization_rate) || 85;
    
    if (weeklyHours > 0 && weeksPerYear > 0) {
      const annualProfit = monthlyProfit * 12;
      const weeklyProfit = annualProfit / weeksPerYear;
      const annualHours = weeklyHours * weeksPerYear;
      const effectiveHours = annualHours * (utilizationRate / 100);
      
      const minutesPerYear = annualHours * 60;
      const ownerProfitPerMinute = minutesPerYear > 0 ? annualProfit / minutesPerYear : 0;
      
      setSettings(prev => {
        const hasChanges = 
          Math.abs(prev.annual_profit_target - annualProfit) > 0.1 ||
          Math.abs(prev.weekly_profit_target - weeklyProfit) > 0.1 ||
          Math.abs(prev.total_annual_hours - annualHours) > 0.1 ||
          Math.abs(prev.effective_hours - effectiveHours) > 0.1 ||
          Math.abs(prev.owner_profit_per_minute - ownerProfitPerMinute) > 0.0001;
        
        if (hasChanges) {
          return {
            ...prev,
            annual_profit_target: annualProfit,
            weekly_profit_target: weeklyProfit,
            total_annual_hours: annualHours,
            effective_hours: effectiveHours,
            owner_profit_per_minute: ownerProfitPerMinute
          };
        }
        return prev;
      });
    }
  }, 500);

  return () => clearTimeout(timer);
}, [
    settings.monthly_profit_target,
    settings.annual_overhead,
    settings.total_working_hours,
    settings.weeks_per_year,
    settings.staff_utilization_rate
  ]);

  const handleInputChange = useCallback((field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleTradingHoursChange = useCallback((hoursData) => {
    console.log('Trading hours changed:', hoursData);
    if (hoursData && typeof hoursData === 'object') {
      setSettings(prev => ({
        ...prev,
        total_working_days: hoursData.totalWorkingDays || 0,
        total_working_hours: hoursData.totalWorkingHours || 0
      }));
    }
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      await calculatorApi.updateBusinessSettings(settings);
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
      if (isComplete) {
        setTimeout(() => navigate('/calculator'), 1500);
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleNavigateToOverheads = () => {
    navigate('/calculator/overheads');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 4, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Typography variant="h4" sx={{ color: 'white', mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <SettingsIcon sx={{ fontSize: 40 }} />
          Business Settings
        </Typography>
        <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
          Configure your business foundation for accurate pricing calculations
        </Typography>
        
        {/* Progress Indicator */}
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: 'white' }}>
              Setup Progress
            </Typography>
            <Chip 
              label={isComplete ? 'Complete' : 'In Progress'}
              icon={isComplete ? <CheckCircle /> : <Warning />}
              sx={{ 
                backgroundColor: isComplete ? 'rgba(76, 175, 80, 0.9)' : 'rgba(255, 193, 7, 0.9)',
                color: 'white'
              }}
            />
          </Box>
          <Box sx={{ 
            width: '100%', 
            height: 8, 
            bgcolor: 'rgba(255,255,255,0.3)', 
            borderRadius: 4,
            overflow: 'hidden'
          }}>
            <Box sx={{ 
              width: `${isComplete ? 100 : 60}%`, 
              height: '100%', 
              bgcolor: 'white',
              transition: 'width 0.3s ease'
            }} />
          </Box>
        </Box>
      </Paper>

      {/* Alerts */}
      {!isComplete && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Setup Required:</strong> Complete all sections below for accurate pricing calculations.
        </Alert>
      )}
      
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profit & Revenue */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Profit & Revenue</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="Monthly Profit Target (£)"
                type="number"
                value={settings.monthly_profit_target}
                onChange={(e) => handleInputChange('monthly_profit_target', e.target.value)}
                helperText="Your desired monthly profit after taxes"
                sx={{ mb: 2 }}
              />
              
              {settings.monthly_profit_target && (
                <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Annual Target:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        £{parseFloat(settings.annual_profit_target || 0).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Weekly Target:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        £{parseFloat(settings.weekly_profit_target || 0).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Per Minute:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        £{parseFloat(settings.owner_profit_per_minute || 0).toFixed(4)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Trading Hours */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Trading Hours</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {tradingHoursData !== null ? (
                <CompactTradingHours
                  initialData={tradingHoursData}
                  onChange={handleTradingHoursChange}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              
              {settings.total_working_hours > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Working Days:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {settings.total_working_days} days/week
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Total Hours:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {settings.total_working_hours} hrs/week
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Overhead Costs */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountBalance sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Overhead Costs</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Overhead costs are calculated from your actual expenses in the system
              </Typography>
              
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Annual Overhead:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      £{parseFloat(settings.annual_overhead || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Per Minute:</Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                      £{parseFloat(settings.overhead_per_minute || 0).toFixed(4)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              
              <Button
                fullWidth
                variant="outlined"
                onClick={handleNavigateToOverheads}
              >
                Manage Overheads
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Scheduling Settings */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Scheduling Settings</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="Working Weeks per Year"
                type="number"
                value={settings.weeks_per_year}
                onChange={(e) => handleInputChange('weeks_per_year', e.target.value)}
                helperText="Typically 48-52 weeks"
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Staff Utilization Rate (%)"
                type="number"
                value={settings.staff_utilization_rate}
                onChange={(e) => handleInputChange('staff_utilization_rate', e.target.value)}
                helperText="% of time spent on paid services"
                inputProps={{ min: 1, max: 100 }}
              />
              
              {settings.weeks_per_year && settings.staff_utilization_rate && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Annual Hours:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {parseFloat(settings.total_annual_hours || 0).toFixed(0)} hrs
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Effective Hours:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        {parseFloat(settings.effective_hours || 0).toFixed(0)} hrs
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* VAT Settings */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountBalance sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">VAT Settings</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="VAT Rate (%)"
                type="number"
                value={settings.vat_rate}
                onChange={(e) => handleInputChange('vat_rate', e.target.value)}
                helperText="Usually 20% in the UK"
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={settings.is_vat_registered}
                  onChange={(e) => handleInputChange('is_vat_registered', e.target.checked)}
                  style={{ marginRight: 8 }}
                  aria-label="Business is VAT Registered"
                />
                <Typography variant="body2">
                  Business is VAT Registered
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : isComplete ? <CheckCircle /> : null}
          sx={{
            minWidth: 300,
            py: 2,
            background: isComplete 
              ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}
        >
          {saving ? 'Saving...' : isComplete ? 'Save & Continue to Calculator' : 'Save Business Settings'}
        </Button>
      </Box>

      {!saveSuccess && settings.monthly_profit_target && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>Don't forget to save!</strong> Your changes haven't been saved yet.
        </Alert>
      )}
    </Container>
  );
};

export default BusinessSettings;